import { useCallback, useEffect, useState } from "react";
import { Info, KeyRound, Plug, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { PriceEngineCard } from "@/components/PriceEngineCard";
import { StalePricesCard } from "@/components/StalePricesCard";
import { ProviderCoverageCard } from "@/components/ProviderCoverageCard";
import { providerService } from "@/services/providerService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const formatInterval = (seconds) => {
  if (!seconds) return "—";
  if (seconds % 86400 === 0) return `${seconds / 86400} dia(s)`;
  if (seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds}s`;
};
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ADMIN_NAV } from "./nav";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white focus-visible:ring-[#2563EB]";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

const STATUS_COLOR = { ok: "text-[#10B981]", error: "text-[#EF4444]", unknown: "text-[#94A3B8]" };const KEY_COLOR = { active: "text-[#10B981]", reserve: "text-[#F59E0B]", disabled: "text-[#EF4444]" };

export default function AdminProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [keyDialog, setKeyDialog] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [form, setForm] = useState({ label: "", api_key: "", status: "reserve", priority: 1 });
  const [testing, setTesting] = useState("");

  const load = useCallback(async () => {
    try {
      setProviders(await providerService.list());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleProvider = async (p) => {
    try {
      await providerService.update(p.slug, { enabled: !p.enabled });
      toast.success(`${p.name} ${p.enabled ? "desativado" : "ativado"}`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const saveInterval = async (p, patch) => {
    try {
      await providerService.update(p.slug, patch);
      toast.success(`Intervalo de ${p.name} atualizado`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const runTest = async (p) => {    setTesting(p.slug);
    try {
      const r = await providerService.test(p.slug);
      r.success ? toast.success(`${p.name}: ${r.message}`) : toast.error(`${p.name}: ${r.message}`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setTesting("");
    }
  };

  const addKey = async (e) => {
    e.preventDefault();
    try {
      await providerService.addKey(keyDialog.slug, { ...form, priority: Number(form.priority) || 1 });
      toast.success("Chave adicionada");
      setKeyDialog(null);
      setForm({ label: "", api_key: "", status: "reserve", priority: 1 });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const saveKey = async (e) => {
    e.preventDefault();
    try {
      const payload = { label: editKey.label, status: editKey.status, priority: Number(editKey.priority) || 1 };
      if (editKey.new_key) payload.api_key = editKey.new_key;
      await providerService.updateKey(editKey.id, payload);
      toast.success("Chave atualizada");
      setEditKey(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const removeKey = async (k) => {
    if (!window.confirm(`Remover a chave "${k.label}"?`)) return;
    try {
      await providerService.deleteKey(k.id);
      toast.success("Chave removida");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="APIs e Provedores" subtitle="Gestão de chaves sem alterar código nem fazer deploy">
      <div data-testid="admin-providers-page" className="space-y-6">
        <PriceEngineCard />
        <StalePricesCard />
        <ProviderCoverageCard />
        {providers.map((p) => (
          <section
            key={p.slug}
            data-testid={`provider-card-${p.slug}`}
            className="rounded-md border border-[#22252F] bg-[#12141A] p-6 transition-colors hover:border-[#2563EB]/30 sm:p-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Plug className="h-4 w-4 text-[#60A5FA]" />
                  <h2 className="font-heading text-lg font-light tracking-tight text-white md:text-lg">{p.name}</h2>
                  <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${STATUS_COLOR[p.status] || ""}`}
                        data-testid={`provider-status-${p.slug}`}>
                    {p.requires_key && p.keys_total === 0 ? "sem chave" : p.status}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748B]">
                    {p.refresh_seconds}s
                  </span>
                </div>
                <p className="max-w-2xl text-sm text-[#94A3B8]">{p.role || "—"}</p>
                <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  {p.markets.map((m) => (
                    <span key={m} className="border border-[#22252F] px-2 py-1">{m}</span>
                  ))}
                </div>
                {p.last_connection_test ? (
                  <p className="font-mono text-[11px] text-[#94A3B8]">
                    Último teste: {fmtDate(p.last_connection_test)} · {p.last_test_message}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-end gap-3" data-testid={`interval-${p.slug}`}>
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Chamar a cada</p>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        data-testid={`interval-value-${p.slug}`}
                        defaultValue={p.call_interval_value ?? p.call_interval_seconds ?? 60}
                        onBlur={(e) => saveInterval(p, { call_interval_value: Number(e.target.value) })}
                        className="h-9 w-24 rounded-md border border-[#22252F] bg-[#0F1116] px-2 font-mono text-xs text-white"
                      />
                      <select
                        data-testid={`interval-unit-${p.slug}`}
                        defaultValue={p.call_interval_unit || "seconds"}
                        onChange={(e) => saveInterval(p, { call_interval_unit: e.target.value })}
                        className="h-9 rounded-md border border-[#22252F] bg-[#0F1116] px-2 text-xs text-white"
                      >
                        <option value="seconds">segundos</option>
                        <option value="minutes">minutos</option>
                        <option value="hours">horas</option>
                        <option value="days">dias</option>
                      </select>
                    </div>
                  </div>
                  <p data-testid={`interval-summary-${p.slug}`} className="text-xs text-[#94A3B8]">
                    Intervalo ativo: {p.call_interval_seconds}s · chamadas hoje: {p.usage?.per_day ?? 0}
                  </p>
                </div>
                {p.free_tier ? (
                  <div data-testid={`free-tier-note-${p.slug}`}
                       className="rounded-md border border-[#2563EB]/30 bg-[#2563EB]/5 p-3 text-xs text-[#94A3B8]">
                    <div className="mb-2 flex items-center gap-2 text-[#60A5FA]">
                      <Info className="h-3.5 w-3.5" />
                      <span className="uppercase tracking-[0.2em]">Limites do plano gratuito</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono">
                      <span>por min: {p.free_tier.per_minute ?? "—"}</span>
                      <span>por hora: {p.free_tier.per_hour ?? "—"}</span>
                      <span>por dia: {p.free_tier.per_day ?? "—"}</span>
                      <span className="text-[#10B981]">
                        recomendado: 1 ciclo / {formatInterval(p.free_tier.recommended_interval_seconds)}
                      </span>
                    </div>
                    <p className="mt-2">{p.free_tier.note}</p>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={p.enabled} onCheckedChange={() => toggleProvider(p)}
                          data-testid={`toggle-provider-${p.slug}`} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                    {p.enabled ? "ativo" : "inativo"}
                  </span>
                </div>
                <Button onClick={() => runTest(p)} disabled={testing === p.slug}
                        data-testid={`test-provider-${p.slug}`}
                        className="h-10 rounded-md border border-[#22252F] bg-transparent text-xs uppercase tracking-[0.15em] text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                  <RefreshCw className={`mr-2 h-3.5 w-3.5 ${testing === p.slug ? "animate-spin" : ""}`} />
                  Testar
                </Button>
                <Button onClick={() => setKeyDialog(p)} data-testid={`add-key-${p.slug}`}
                        className="h-10 rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] transition-colors hover:bg-[#1D4ED8]">
                  <Plus className="mr-2 h-3.5 w-3.5" /> Chave
                </Button>
              </div>
            </div>

            <div className="mt-6 overflow-x-auto border-t border-[#22252F] pt-4">
              {p.keys.length === 0 ? (
                <p className="py-4 text-sm text-[#64748B]">
                  {p.requires_key ? "Nenhuma chave configurada" : "Este provedor não requer chave"}
                </p>
              ) : (
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                    <tr>
                      <th className="py-3">Etiqueta</th>
                      <th className="py-3">Chave</th>
                      <th className="py-3">Estado</th>
                      <th className="py-3 text-right">Prioridade</th>
                      <th className="py-3 text-right">Chamadas</th>
                      <th className="py-3 text-right">Erros</th>
                      <th className="py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody data-testid={`keys-body-${p.slug}`}>
                    {p.keys.map((k) => (
                      <tr key={k.id} className="border-t border-[#1A1D25]">
                        <td className="py-3 text-white">{k.label}</td>
                        <td className="py-3 font-mono text-xs text-[#94A3B8]" data-testid={`masked-key-${k.id}`}>{k.masked_key}</td>
                        <td className={`py-3 font-mono text-xs uppercase ${KEY_COLOR[k.status]}`}>{k.status}</td>
                        <td className="py-3 text-right font-mono text-[#94A3B8]">{k.priority}</td>
                        <td className="py-3 text-right font-mono text-[#94A3B8]">{k.calls_count}</td>
                        <td className="py-3 text-right font-mono text-[#94A3B8]">{k.errors_count}</td>
                        <td className="py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditKey({ ...k, new_key: "" })} data-testid={`edit-key-${k.id}`}
                                    className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                              Editar
                            </button>
                            <button onClick={() => removeKey(k)} data-testid={`delete-key-${k.id}`}
                                    className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        ))}
      </div>

      <Dialog open={!!keyDialog} onOpenChange={(o) => !o && setKeyDialog(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="add-key-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light tracking-tight">
              Nova chave · {keyDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addKey} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Etiqueta</Label>
              <Input required data-testid="key-label-input" value={form.label}
                     onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="API Key 1" className={field} /></div>
            <div className="space-y-2"><Label className={labelCls}>Chave</Label>
              <Input required data-testid="key-value-input" value={form.api_key}
                     onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                     placeholder="sk_live_..." className={`${field} font-mono`} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Estado</Label>
                <select data-testid="key-status-select" value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  <option value="active">active</option>
                  <option value="reserve">reserve</option>
                  <option value="disabled">disabled</option>
                </select></div>
              <div className="space-y-2"><Label className={labelCls}>Prioridade</Label>
                <Input type="number" min="1" data-testid="key-priority-input" value={form.priority}
                       onChange={(e) => setForm({ ...form, priority: e.target.value })} className={`${field} font-mono`} /></div>
            </div>
            <p className="flex items-center gap-2 text-xs text-[#64748B]">
              <KeyRound className="h-3.5 w-3.5" /> A chave é cifrada e nunca mostrada por completo.
            </p>
            <DialogFooter>
              <Button type="submit" data-testid="submit-key" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editKey} onOpenChange={(o) => !o && setEditKey(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="edit-key-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Editar chave</DialogTitle></DialogHeader>
          <form onSubmit={saveKey} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Etiqueta</Label>
              <Input data-testid="edit-key-label" value={editKey?.label || ""}
                     onChange={(e) => setEditKey({ ...editKey, label: e.target.value })} className={field} /></div>
            <div className="space-y-2"><Label className={labelCls}>Nova chave (vazio = manter)</Label>
              <Input data-testid="edit-key-value" value={editKey?.new_key || ""}
                     onChange={(e) => setEditKey({ ...editKey, new_key: e.target.value })}
                     placeholder={editKey?.masked_key} className={`${field} font-mono`} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Estado</Label>
                <select data-testid="edit-key-status" value={editKey?.status || "reserve"}
                        onChange={(e) => setEditKey({ ...editKey, status: e.target.value })}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  <option value="active">active</option>
                  <option value="reserve">reserve</option>
                  <option value="disabled">disabled</option>
                </select></div>
              <div className="space-y-2"><Label className={labelCls}>Prioridade</Label>
                <Input type="number" min="1" data-testid="edit-key-priority" value={editKey?.priority || 1}
                       onChange={(e) => setEditKey({ ...editKey, priority: e.target.value })} className={`${field} font-mono`} /></div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-edit-key" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
